import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, Check, Crown, Star, Sparkles, Newspaper, Globe2, Radio, Users, Heart, Gift, Mail, PhoneCall, Landmark } from "lucide-react";

const IMG = {
  lamp: "https://images.unsplash.com/photo-1701093919822-899072e02c41?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  tea: "https://images.unsplash.com/photo-1701546858735-7354d14126c6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  call: "https://images.unsplash.com/photo-1758686254023-09e191043c88?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  music: "https://images.unsplash.com/photo-1641465431157-4fa4f99580e6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const TIERS = [
  {
    id: "title", icon: Crown, label: "Title Partner", tag: "The face of Neox",
    color: "#F5C97A",
    points: [
      "“Neox — presented by [Your Brand]” across the app & national launch",
      "Logo on the splash, home screen, launch backdrop & every press release",
      "Category exclusivity — no competing brand",
      "Co-marketing to elders and their diaspora families",
      "For a telco: option to zero-rate the app's data for all seniors",
    ],
  },
  {
    id: "platinum", icon: Star, label: "Platinum Partner", tag: "Premium visibility",
    color: "#E5E4E2",
    points: [
      "Logo on the home screen + a sponsored Room or Song of the Day",
      "Named recognition at the launch press conference",
      "Feature in social posts & the shareable Post Kit",
    ],
  },
  {
    id: "gold", icon: Sparkles, label: "Gold Partner", tag: "CSR goodwill",
    color: "#D4A24E",
    points: [
      "Logo on the Sponsors wall inside the app",
      "Monthly “Song of the Day” sponsorship",
      "Recognition at the launch & in media write-ups",
    ],
  },
  {
    id: "media", icon: Newspaper, label: "Official Media Partner", tag: "Coverage for branding",
    color: "#9DB4C0",
    points: [
      "Exclusive first coverage of a Sri Lankan first",
      "Launch broadcast / write-up & ongoing features",
      "Branding across all Neox announcements in return",
    ],
  },
];

const STREAMS = [
  { icon: Landmark, t: "Sponsorship & CSR", d: "Corporate partners fund the platform — the primary revenue engine." },
  { icon: Gift, t: "Diaspora “Gift to my parents”", d: "Children abroad gift a yearly membership; parents use it free." },
  { icon: Heart, t: "Memorial dedications", d: "Families dedicate a song in loving memory — a gift of merit (pin)." },
];

const STATS = [
  { n: "3.9M", l: "Sri Lankans aged 60+ — 18% of the nation (2024 Census)" },
  { n: "1 in 4", l: "Will be elderly by 2041 — a fast-aging nation" },
  { n: "704,795", l: "Public-sector pensioners with steady monthly income" },
  { n: "US$6.6bn", l: "Sent home by the diaspora in 2024 (+10%)" },
];

const DIASPORA = [
  { c: "United Kingdom", n: "700,000" },
  { c: "United Arab Emirates", n: "320,000" },
  { c: "Canada", n: "200,000+" },
  { c: "Australia", n: "186,000" },
  { c: "India", n: "185,000" },
  { c: "Italy (mostly Sinhala)", n: "110,000" },
];

const MARKET = [
  { icon: Users, t: "A large, growing audience", d: "3.92 million Sri Lankans are aged 60+ — 18% of the country, up from just 6.6% in 1981. By 2041, one in four will be an elder. This audience only grows." },
  { icon: Landmark, t: "Real, reachable buying power", d: "704,795 public-sector pensioners receive a dependable state pension (LKR 372bn/yr). Nearly half of all pension value sits with the wealthiest 20% — a genuine disposable-income segment brands want to reach." },
  { icon: Globe2, t: "A global family that pays", d: "Over 2 million Sri Lankans live abroad and sent home US$6.6bn in 2024 (+10%). These are children who actively spend to care for their parents back home." },
];

const EditSlot = ({ children, testid, light }) => (
  <span data-testid={testid} className="inline-block font-ui text-sm font-semibold px-3 py-1.5 rounded-lg"
    style={light
      ? { background: "var(--brass-light)", color: "var(--accent)", border: "1px dashed var(--accent)" }
      : { background: "rgba(245,201,122,0.15)", color: "#F5C97A", border: "1px dashed rgba(245,201,122,0.6)" }}>
    ✎ {children}
  </span>
);

const printStyles = `
@media print {
  .no-print { display: none !important; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  .pk-hero { height: 320px !important; }
  section { break-inside: avoid; }
}
`;

export default function Partner() {
  const doPrint = () => window.print();

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <style>{printStyles}</style>

      {/* Top bar */}
      <div className="no-print sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6 h-16" style={{ background: "var(--bg)", borderBottom: "1px solid var(--border)" }}>
        <Link to="/rooms/sindu-kamare/admin" data-testid="partner-back" className="inline-flex items-center gap-2 font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>
          <ArrowLeft size={16} /> Back
        </Link>
        <button onClick={doPrint} data-testid="partner-print" className="inline-flex items-center gap-2 h-11 px-5 rounded-full font-ui font-bold text-sm transition-transform active:scale-95 hover:scale-[1.02]" style={{ background: "var(--accent)", color: "#fff" }}>
          <Printer size={16} /> Print / Save as brochure
        </button>
      </div>

      {/* Hero */}
      <div className="pk-hero relative h-[75vh] min-h-[460px] flex items-center justify-center text-center overflow-hidden">
        <img src={IMG.lamp} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,14,8,0.55) 0%, rgba(20,14,8,0.82) 100%)" }} />
        <div className="relative px-5 max-w-3xl">
          <span className="inline-block font-ui text-xs font-bold uppercase tracking-[3px] px-4 py-1.5 rounded-full mb-5" style={{ background: "rgba(245,201,122,0.18)", color: "#F5C97A", border: "1px solid rgba(245,201,122,0.5)" }}>
            🪔 A Sri Lankan First
          </span>
          <h1 className="font-en text-4xl sm:text-6xl font-extrabold text-white leading-tight">Partner with Neox</h1>
          <p className="font-en text-lg sm:text-2xl text-white/85 mt-5 font-semibold">The first honourable digital home for our elders —<br className="hidden sm:block" /> free for every pensioner, funded by those who care.</p>
          <p className="font-si text-base text-white/70 mt-4">අපේ වැඩිහිටියන්ට ගෞරවනීය තෑග්ගක් 🌸</p>
        </div>
      </div>

      {/* The story */}
      <section className="max-w-5xl mx-auto px-5 sm:px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
        <div className="rounded-3xl overflow-hidden" style={{ border: "2px solid var(--border)" }}>
          <img src={IMG.tea} alt="" className="w-full h-72 object-cover" />
        </div>
        <div>
          <h2 className="font-en text-3xl font-extrabold" style={{ color: "var(--text)" }}>Why Neox exists</h2>
          <p className="font-ui text-base mt-4 leading-relaxed" style={{ color: "var(--text-2)" }}>
            Across Sri Lanka, our elders manage medicine, food and every small need from a modest pension. Their children have grown, many gone abroad. The songs, the pirith, the poems that shaped their lives slowly fell silent.
          </p>
          <p className="font-ui text-base mt-4 leading-relaxed" style={{ color: "var(--text-2)" }}>
            <b style={{ color: "var(--text)" }}>Neox brings that world back</b> — old Sinhala songs, calm chants, folk poetry and a karaoke room to sing along — in a warm, simple app made for their eyes and hands. And it stays <b style={{ color: "var(--accent)" }}>completely free for them</b>. Always.
          </p>
        </div>
      </section>

      {/* Stats band */}
      <section className="py-14" style={{ background: "var(--card)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {STATS.map((s) => (
            <div key={s.l}>
              <p className="font-en text-4xl sm:text-5xl font-extrabold" style={{ color: "var(--accent)" }}>{s.n}</p>
              <p className="font-ui text-sm mt-2" style={{ color: "var(--text-muted)" }}>{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Market opportunity — by the numbers */}
      <section className="max-w-5xl mx-auto px-5 sm:px-6 py-16">
        <h2 className="font-en text-3xl font-extrabold text-center" style={{ color: "var(--text)" }}>The opportunity — by the numbers</h2>
        <p className="font-ui text-sm text-center mt-2 max-w-xl mx-auto" style={{ color: "var(--text-muted)" }}>A large, loyal, fast-growing audience at home — and a global family that funds it.</p>

        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {MARKET.map((m) => (
            <div key={m.t} className="rounded-3xl p-6" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
              <span className="grid place-items-center w-12 h-12 rounded-2xl" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><m.icon size={22} /></span>
              <h3 className="font-en text-lg font-bold mt-4" style={{ color: "var(--text)" }}>{m.t}</h3>
              <p className="font-ui text-sm mt-2 leading-relaxed" style={{ color: "var(--text-muted)" }}>{m.d}</p>
            </div>
          ))}
        </div>

        {/* Diaspora breakdown */}
        <div className="rounded-3xl p-6 sm:p-8 mt-6" style={{ background: "var(--card)", border: "2px solid var(--border)" }} data-testid="diaspora-table">
          <h3 className="font-en text-xl font-bold" style={{ color: "var(--text)" }}>Where our diaspora lives</h3>
          <p className="font-ui text-xs mt-1" style={{ color: "var(--text-muted)" }}>Sinhala &amp; Tamil families across the wealthy world — your paying, gifting audience.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
            {DIASPORA.map((d) => (
              <div key={d.c} className="rounded-2xl p-4" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                <p className="font-en text-2xl font-extrabold" style={{ color: "var(--accent)" }}>{d.n}</p>
                <p className="font-ui text-xs mt-1" style={{ color: "var(--text-2)" }}>{d.c}</p>
              </div>
            ))}
          </div>
          <p className="font-ui text-xs mt-4" style={{ color: "var(--text-muted)" }}>Remittances 2024: ~50% from the Middle East (UAE, Kuwait, Saudi, Qatar); fast-growing inflows from Canada &amp; the USA.</p>
        </div>

        {/* Provincial concentration — EDITABLE placeholder to fill */}
        <div className="rounded-3xl p-6 mt-6" style={{ background: "var(--bg)", border: "2px dashed var(--border)" }} data-testid="provincial-placeholder">
          <p className="font-ui text-sm font-bold" style={{ color: "var(--text)" }}>📍 Elder concentration by province <span className="font-normal" style={{ color: "var(--text-muted)" }}>— to add</span></p>
          <p className="font-ui text-xs mt-1 mb-3" style={{ color: "var(--text-muted)" }}>Drop in the provincial/district breakdown once you have it (e.g. Western, Southern, Central…). This helps target regional sponsors.</p>
          <EditSlot testid="provincial-edit" light>Add provincial elder distribution here</EditSlot>
        </div>

        <p className="font-ui text-[11px] mt-6 text-center" style={{ color: "var(--text-muted)" }}>
          Sources: Sri Lanka Census of Population &amp; Housing 2024 (Dept. of Census &amp; Statistics); Central Bank of Sri Lanka, External Sector 2024; Institute of Policy Studies. Figures indicative.
        </p>
      </section>

      {/* Why partners win */}
      <section className="max-w-5xl mx-auto px-5 sm:px-6 py-16">
        <h2 className="font-en text-3xl font-extrabold text-center" style={{ color: "var(--text)" }}>Why a brand should partner</h2>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {[
            { icon: Users, t: "Trusted reach to seniors", d: "Reach a loyal, hard-to-access elder audience — and the diaspora families who love them — through a platform they genuinely cherish." },
            { icon: Heart, t: "Unmatched CSR goodwill", d: "Be the brand that honoured Sri Lanka's elders. Dignified, emotional, headline-worthy — the finest kind of CSR." },
            { icon: Globe2, t: "A national first", d: "Association with a first-of-its-kind initiative, launched with press and prestige. Category exclusivity for the title partner." },
          ].map((c) => (
            <div key={c.t} className="rounded-3xl p-6" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
              <span className="grid place-items-center w-12 h-12 rounded-2xl" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><c.icon size={22} /></span>
              <h3 className="font-en text-lg font-bold mt-4" style={{ color: "var(--text)" }}>{c.t}</h3>
              <p className="font-ui text-sm mt-2 leading-relaxed" style={{ color: "var(--text-muted)" }}>{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Sponsor tiers */}
      <section className="py-16" style={{ background: "var(--card)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-6">
          <h2 className="font-en text-3xl font-extrabold text-center" style={{ color: "var(--text)" }}>Partnership tiers</h2>
          <p className="font-ui text-sm text-center mt-2" style={{ color: "var(--text-muted)" }}>Investment on application. Tailored to your brand's goals.</p>
          <div className="grid md:grid-cols-2 gap-5 mt-10">
            {TIERS.map((tier) => (
              <div key={tier.id} data-testid={`tier-${tier.id}`} className="rounded-3xl p-6" style={{ background: "var(--bg)", border: `2px solid ${tier.id === "title" ? "var(--accent)" : "var(--border)"}` }}>
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-12 h-12 rounded-2xl shrink-0" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><tier.icon size={22} /></span>
                  <div>
                    <h3 className="font-en text-xl font-extrabold" style={{ color: "var(--text)" }}>{tier.label}</h3>
                    <p className="font-ui text-xs font-bold uppercase tracking-wide" style={{ color: "var(--accent)" }}>{tier.tag}</p>
                  </div>
                </div>
                <ul className="mt-4 space-y-2">
                  {tier.points.map((p, i) => (
                    <li key={i} className="flex gap-2 font-ui text-sm" style={{ color: "var(--text-2)" }}>
                      <Check size={16} className="mt-0.5 shrink-0" style={{ color: "var(--accent)" }} /> <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Revenue model + diaspora image */}
      <section className="max-w-5xl mx-auto px-5 sm:px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="font-en text-3xl font-extrabold" style={{ color: "var(--text)" }}>A real business — not a charity</h2>
          <p className="font-ui text-base mt-4 leading-relaxed" style={{ color: "var(--text-2)" }}>
            The elder never pays and is never sold to. Revenue comes from those who give <i>on their behalf</i> — sponsors, and children who gift it to their parents. The platform runs on roughly <b style={{ color: "var(--accent)" }}>$10 a month</b>, so partner support is almost pure margin toward a sustainable, growing venture.
          </p>
          <div className="space-y-3 mt-6">
            {STREAMS.map((s) => (
              <div key={s.t} className="flex gap-3 rounded-2xl p-4" style={{ background: "var(--card)", border: "1px solid var(--border)" }}>
                <span className="grid place-items-center w-10 h-10 rounded-xl shrink-0" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><s.icon size={18} /></span>
                <div>
                  <p className="font-ui text-sm font-bold" style={{ color: "var(--text)" }}>{s.t}</p>
                  <p className="font-ui text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl overflow-hidden" style={{ border: "2px solid var(--border)" }}>
          <img src={IMG.call} alt="" className="w-full h-full max-h-[520px] object-cover" />
        </div>
      </section>

      {/* Founder note */}
      <section className="max-w-3xl mx-auto px-5 sm:px-6 pb-16">
        <div className="rounded-3xl p-8 text-center" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
          <img src={IMG.music} alt="" className="w-20 h-20 rounded-full object-cover mx-auto mb-4" style={{ border: "3px solid var(--accent)" }} />
          <p className="font-ui text-base italic leading-relaxed" style={{ color: "var(--text-2)" }}>
            “We built Neox so no elder feels forgotten. Partner with us, and your brand becomes part of a quiet, national act of kindness — one that our elders, and their families around the world, will never forget.”
          </p>
          <p className="font-en text-sm font-bold mt-4" style={{ color: "var(--text)" }}>The Neox Team</p>
          <p className="font-ui text-xs" style={{ color: "var(--text-muted)" }}>A FutureTec Production</p>
        </div>
      </section>

      {/* Our Partners — logo wall (fill as you sign sponsors) */}
      <section className="max-w-5xl mx-auto px-5 sm:px-6 py-16" data-testid="partners-wall">
        <h2 className="font-en text-3xl font-extrabold text-center" style={{ color: "var(--text)" }}>Our partners</h2>
        <p className="font-ui text-sm text-center mt-2" style={{ color: "var(--text-muted)" }}>Proudly supported by — <span style={{ color: "var(--accent)" }}>your brand could be first</span>.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} data-testid={`partner-logo-slot-${i}`} className="h-24 rounded-2xl grid place-items-center" style={{ background: "var(--card)", border: "2px dashed var(--border)" }}>
              <span className="font-ui text-xs font-semibold" style={{ color: "var(--text-muted)" }}>✎ Your logo here</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 text-center" style={{ background: "linear-gradient(135deg, #1C140E 0%, #3B2A16 60%, #92400E 160%)" }}>
        <div className="max-w-2xl mx-auto px-5">
          <h2 className="font-en text-3xl sm:text-4xl font-extrabold text-white">Become a Founding Partner</h2>
          <p className="font-ui text-base text-white/80 mt-3">Be first. Lead a Sri Lankan first — and honour our elders.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-7">
            <span data-testid="partner-email" className="inline-flex items-center gap-2 font-ui text-sm font-semibold px-4 py-2.5 rounded-full" style={{ background: "rgba(245,201,122,0.15)", color: "#F5C97A", border: "1px dashed rgba(245,201,122,0.6)" }}>
              <Mail size={16} /> ✎ [ add your email ]
            </span>
            <span data-testid="partner-phone" className="inline-flex items-center gap-2 font-ui text-sm font-semibold px-4 py-2.5 rounded-full" style={{ background: "rgba(245,201,122,0.15)", color: "#F5C97A", border: "1px dashed rgba(245,201,122,0.6)" }}>
              <PhoneCall size={16} /> ✎ [ +94 __ ___ ____ ]
            </span>
          </div>
          <p className="font-ui text-xs text-white/60 mt-4">Tap-to-edit placeholders — add your real contact details before you share this deck.</p>
          <p className="font-ui text-xs text-white/50 mt-4">neox.lk · Free forever for every Sri Lankan pensioner 🪔</p>
        </div>
      </section>
    </div>
  );
}
